/**
 * Content Management Types
 * Types for managing flipbooks, images, videos, and text content
 */

import type { BaseEntity, UUID } from "./common.types";

// Content type enum
export enum ContentType {
  FLIPBOOK = "FLIPBOOK",
  IMAGE = "IMAGE",
  VIDEO_LINK = "VIDEO_LINK",
  TEXT_BLOCK = "TEXT_BLOCK",
  FILE = "FILE",
  WEBPAGE_CHAT = "WEBPAGE_CHAT",
}

// Video platform enum
export enum VideoPlatform {
  YOUTUBE = "YOUTUBE",
  VIMEO = "VIMEO",
  WISTIA = "WISTIA",
  OTHER = "OTHER",
}

// Content format for text blocks
export enum ContentFormat {
  MARKDOWN = "markdown",
  HTML = "html",
  PLAIN = "plain",
}

// Processing status for flipbooks
export enum ProcessingStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  READY = "ready",
  FAILED = "failed",
}

// =====================================================
// CONTENT ITEM INTERFACES
// =====================================================

/**
 * Base content item - master record for all content types
 */
export interface ContentItem extends BaseEntity {
  title: string;
  description?: string;
  content_type: ContentType;
  tags: string[];
  notes?: string;
  is_public: boolean;
  is_featured?: boolean;
  folder_id?: UUID | null;
  created_by?: UUID;
  organization_id?: UUID;
  // Analytics
  thumbnail_url?: string;
  view_count?: number;
  download_count?: number;
  // Joined creator data
  creator?: {
    id: UUID;
    email: string;
    full_name?: string;
  };
  // Type-specific details (populated from related tables)
  details?:
    | FlipbookDetails
    | ImageDetails
    | VideoLinkDetails
    | TextBlockDetails
    | FileDetails
    | WebpageChatDetails;
}

/**
 * Flipbook-specific details
 */
export interface FlipbookDetails {
  id: UUID;
  content_item_id: UUID;
  page_count: number;
  page_urls?: string[]; // Array of page image URLs
  storage_path: string;
  processing_status: ProcessingStatus;
  processed_at?: string;
  publicUrl?: string; // Generated public URL for the PDF
  pages?: FlipbookPage[]; // Page image URLs for the viewer
}

/**
 * Individual flipbook page (for viewer)
 */
export interface FlipbookPage {
  pageNumber: number;
  imageUrl: string;
}

/**
 * Image-specific details
 */
export interface ImageDetails {
  id: UUID;
  content_item_id: UUID;
  storage_path: string;
  alt_text?: string;
  width?: number;
  height?: number;
  file_size?: number;
  mime_type?: string;
  publicUrl?: string;
}

/**
 * Video link details
 */
export interface VideoLinkDetails {
  id: UUID;
  content_item_id: UUID;
  url: string;
  platform?: VideoPlatform;
  video_id?: string;
  duration_seconds?: number;
  thumbnail_url?: string;
}

/**
 * Text block details
 */
export interface TextBlockDetails {
  id: UUID;
  content_item_id: UUID;
  content?: string;
  content_format: ContentFormat;
}

/**
 * File details (generic downloadable files)
 */
export interface FileDetails {
  id: UUID;
  content_item_id: UUID;
  storage_path: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
  publicUrl?: string;
}

/**
 * Webpage chat details (AI chat for crawled webpages)
 */
export interface WebpageChatDetails {
  id: UUID;
  content_item_id: UUID;
  source_url: string;
  allowed_domains: string[];
  crawl_status: "pending" | "crawling" | "ready" | "failed";
  crawled_at?: string;
  page_title?: string;
  page_content_length?: number;
}

// =====================================================
// INPUT INTERFACES
// =====================================================

/**
 * Input for creating a content item
 */
export interface CreateContentItemInput {
  title: string;
  description?: string;
  content_type: ContentType;
  tags?: string[];
  notes?: string;
  is_public?: boolean;
  organization_id?: UUID;
}

/**
 * Input for updating a content item
 */
export interface UpdateContentItemInput {
  title?: string;
  description?: string;
  tags?: string[];
  notes?: string;
  is_public?: boolean;
  is_featured?: boolean;
  folder_id?: UUID | null;
}

/**
 * Input for creating a flipbook
 */
export interface CreateFlipbookInput {
  content_item_id: UUID;
  page_count: number;
  storage_path: string;
}

/**
 * Input for creating an image
 */
export interface CreateImageInput {
  content_item_id: UUID;
  storage_path: string;
  alt_text?: string;
  width?: number;
  height?: number;
  file_size?: number;
  mime_type?: string;
}

/**
 * Input for creating a video link
 */
export interface CreateVideoLinkInput {
  content_item_id: UUID;
  url: string;
  platform?: VideoPlatform;
  video_id?: string;
  duration_seconds?: number;
  thumbnail_url?: string;
}

/**
 * Input for creating a text block
 */
export interface CreateTextBlockInput {
  content_item_id: UUID;
  content?: string;
  content_format?: ContentFormat;
}

// =====================================================
// FILTER AND QUERY INTERFACES
// =====================================================

/**
 * Filters for querying content items
 */
export interface ContentFilters {
  content_type?: ContentType[];
  tags?: string[];
  search?: string;
  is_public?: boolean;
  is_featured?: boolean;
  folder_id?: UUID | null;
  organization_id?: UUID;
  created_by?: UUID;
}

/**
 * Content folder for organizing content items
 */
export interface ContentFolder {
  id: UUID;
  name: string;
  parent_folder_id?: UUID | null;
  organization_id?: UUID | null;
  created_by?: UUID;
  created_at: string;
  updated_at: string;
}

/**
 * Flipbook viewer data returned from API
 */
export interface FlipbookViewerData {
  totalPages: number;
  pdfDownloadUrl: string;
  html: string; // Pre-rendered HTML with page images
  pages: FlipbookPage[];
  processingStatus?: string; // 'pending' | 'processing' | 'ready' | 'failed'
}

// =====================================================
// CONSTANTS
// =====================================================

/**
 * Content type options for dropdowns
 */
export const CONTENT_TYPE_OPTIONS = [
  { value: ContentType.FLIPBOOK, label: "Flipbook (PDF)", icon: "document" },
  { value: ContentType.IMAGE, label: "Image", icon: "photo" },
  { value: ContentType.VIDEO_LINK, label: "Video Link", icon: "video-camera" },
  {
    value: ContentType.TEXT_BLOCK,
    label: "Formatted Text",
    icon: "document-text",
  },
  { value: ContentType.FILE, label: "File", icon: "paper-clip" },
  {
    value: ContentType.WEBPAGE_CHAT,
    label: "Webpage Chat",
    icon: "chat-bubble-bottom-center-text",
  },
];

/**
 * Video platform options for dropdowns
 */
export const VIDEO_PLATFORM_OPTIONS = [
  { value: VideoPlatform.YOUTUBE, label: "YouTube" },
  { value: VideoPlatform.VIMEO, label: "Vimeo" },
  { value: VideoPlatform.WISTIA, label: "Wistia" },
  { value: VideoPlatform.OTHER, label: "Other" },
];

/**
 * Content format options for text blocks
 */
export const CONTENT_FORMAT_OPTIONS = [
  { value: ContentFormat.MARKDOWN, label: "Markdown" },
  { value: ContentFormat.HTML, label: "HTML" },
  { value: ContentFormat.PLAIN, label: "Plain Text" },
];

/**
 * Storage bucket name for content files
 */
export const CONTENT_STORAGE_BUCKET = "content";
